import * as SelectPrimitive from "@radix-ui/react-select";
import Card from "src/components/Card";
import Icon from "src/components/field/utils/Icon";
import SelectSearch from "src/components/field/utils/SelectSearch";
import Wrapper, { WrapperProps } from "src/components/field/utils/Wrapper";
import withTranslation, {
	Translation,
} from "src/components/hoc/withTranslation";
import { motion } from "framer-motion";

export type SelectValue = string | string[] | any;

type Option = {
	value: string;
	label: string | JSX.Element;
};

export type SelectTheme = {
	value?: {
		renderValue?: (value: SelectValue) => JSX.Element;
	};
	card?: {
		className?: string;
	};
	item?: {
		indicator?: boolean;
		className?: string;
	};
};

interface SelectProps extends Translation {
	wrapper?: WrapperProps;
	options: Option[];
	placeholder?: string;
	value?: SelectValue;
	onChange: (value?: SelectValue) => void;
	onClose?: () => void;
	search?: {
		onChange: (search: string) => void;
		loading?: boolean;
		placeholder?: string;
		value?: string;
		autoFocus?: boolean;
	};
	create?: {
		value: string;
		render: JSX.Element;
	};
	theme?: SelectTheme;
	isClearable?: boolean;
	empty?: {
		description: string;
	};
}

const Select = ({
	t,
	wrapper,
	options,
	placeholder,
	value,
	onChange,
	onClose,
	search,
	theme,
	isClearable,
	empty,
	create,
}: SelectProps) => {
	const isMultiple = Array.isArray(value);
	const hasValue = isMultiple ? isMultiple && value?.length > 0 : !!value;

	let itemClassName = `cursor-pointer rounded-md`;

	if (theme?.item?.className) {
		itemClassName += ` ${theme?.item?.className}`;
	}

	if (!itemClassName?.includes("radix-disabled")) {
		itemClassName += ` radix-disabled:opacity-30`;
	}

	if (!itemClassName?.includes("radix-highlighted")) {
		itemClassName += ` radix-highlighted:bg-gray`;
	}

	if (!itemClassName?.includes("p-")) {
		itemClassName += ` p-1 `;
	}

	const handleChange = (update: any) => {
		if (!update) {
			onChange(isMultiple ? [] : undefined);
			return;
		}
		if (isMultiple) {
			if (value.includes(update)) {
				onChange(value.filter((item: string) => item !== update));
			} else {
				onChange([...value, update]);
			}
		} else {
			onChange(update);
		}
	};

	return (
		<Wrapper
			{...{
				...wrapper,
			}}
		>
			<div className="relative">
				<SelectPrimitive.Root
					value={!isMultiple ? value : ""}
					onValueChange={handleChange}
				>
					<SelectPrimitive.Trigger aria-label={"test"} asChild>
						<button
							className={`flex items-center gap-2 border-[2px] border-transparent p-1 focus:border-gray rounded-md ${
								!hasValue ? "text-placeholder" : "text-dark"
							}`}
						>
							{!hasValue && (
								<SelectPrimitive.Icon>
									<Icon
										className="cursor-pointer text-dark"
										icon="far fa-angle-down"
									/>
								</SelectPrimitive.Icon>
							)}

							{!hasValue && <span>{t("placeholder")}</span>}

							{hasValue && (
								<>
									{theme?.value?.renderValue ? (
										theme.value.renderValue(
											<SelectPrimitive.Value
												placeholder={
													placeholder ||
													t("placeholder")
												}
											/>
										)
									) : isMultiple ? (
										<SelectPrimitive.Value
											defaultValue={
												value.length
													? value.join(", ")
													: undefined
											}
											placeholder={
												placeholder || t("placeholder")
											}
										/>
									) : (
										<SelectPrimitive.Value
											placeholder={
												placeholder || t("placeholder")
											}
										/>
									)}
								</>
							)}
						</button>
					</SelectPrimitive.Trigger>
					<SelectPrimitive.Content
						onCloseAutoFocus={onClose}
						position="popper"
						className="w-full z-[20] left-0 right-0 py-1 max-w-[220px]"
					>
						<Card contentClassName=" p-2" className="mt-0">
							{search?.onChange && (
								<>
									<SelectSearch {...search} />
									<SelectPrimitive.Separator className="border-gray border" />
								</>
							)}
							<SelectPrimitive.Viewport className="">
								<div className="max-h-60 flex flex-col gap-1">
									{isClearable && hasValue && (
										<SelectPrimitive.Item
											value=""
											textValue={t("placeholder")}
											className={itemClassName}
										>
											<SelectPrimitive.ItemText>
												<div className="flex items-center gap-2">
													<i className="fal fa-times"></i>
													<span>{t("empty")}</span>
												</div>
											</SelectPrimitive.ItemText>
										</SelectPrimitive.Item>
									)}
									{options.length === 0 &&
										empty?.description &&
										!create && (
											<div className="flex flex-col p-4 opacity-20">
												<p>{empty?.description}</p>
											</div>
										)}

									{create?.value && options.length === 0 && (
										<SelectPrimitive.Item
											value={create.value}
											className={`${itemClassName} border border-transparent`}
										>
											{create.render}
										</SelectPrimitive.Item>
									)}

									{options.map((option) => {
										const isSelected =
											option.value === value ||
											(isMultiple &&
												value?.includes(option.value));
										return (
											<SelectPrimitive.Item
												key={option.value}
												value={option.value}
												className={`${itemClassName} flex items-center justify-between`}
												onChange={console.log}
											>
												<SelectPrimitive.ItemText>
													<div className="flex items-center gap-2 justify-between">
														{option.label}
													</div>
												</SelectPrimitive.ItemText>
												{theme?.item?.indicator && (
													<motion.div
														variants={{
															default: {
																scale: 0.9,
																opacity: 0,
															},
															selected: {
																scale: 1,
																opacity: 1,
															},
														}}
														initial="default"
														animate={
															isSelected
																? "selected"
																: "default"
														}
														className="bg-primary w-2 h-2 mr-2 rounded-full"
													></motion.div>
												)}
											</SelectPrimitive.Item>
										);
									})}
								</div>
							</SelectPrimitive.Viewport>
						</Card>
					</SelectPrimitive.Content>
				</SelectPrimitive.Root>
			</div>
		</Wrapper>
	);
};

Select.locale = {
	nl: {
		placeholder: "Leeg",
		empty: "Leeg maken",
	},
	en: {
		placeholder: "Empty",
		empty: "Clear",
	},
};

export default withTranslation(Select);
